import {
  CheckboxType,
  ComponentType,
  convertISOStringToLocalDateString,
  ErrorOption,
  Form,
  FormCheckboxGroup,
  FormSelect,
  FormTextField,
  Grid,
  Icon,
  requiredRule,
  Scrollbar,
} from '@gcl/react-core';
import * as React from 'react';
import { DetailPageTopBar, IDetailPageTopBarItem } from '@app/DetailPageTopBar';
import { LabelWithMessage } from '@common/LabelWithMessage';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { DetailPageLayout } from '@app/DetailPageLayout';
import { routes } from '@routes/routes.models';
import { FETCH_QUERIES_NAME } from '@common/constants';
import { FormTextareaWithMaxLength } from '@core/FormTextareaWithMaxLength';
import { Note } from '@core/Note/Note';
import { ProjectTwcGeRequestDetailSignatureCard } from './ProjectTwcGeRequestDetailSignatureCard';
import { getAccessMode } from '@common/detailPage.hooks';
import { useProjectId, useProjectIsArchived } from '@config/config.hooks';
import { useNavigate } from '@routes/routes.hooks';
import { EDetailPageTopBarItemType } from '@app/detailPageTopBar.model';
import './ProjectTwcGeRequestDetailPage.scss';
import { useFetchAttachmentTwcGeRequests } from '@file/projectFile/projectFile.queries';
import { EProjectFileType } from '@file/projectFile/projectFile.constants';
import { Attachments } from '@file/Attachments';
import { EProjectTwcGeRequestApplicationType, EProjectTwcGeRequestStatus } from '../projectTwcGeRequest.constants';
import { useUpsertTwcGeRequest } from './projectTwcGeRequestDetailsPage.hooks';
import { ProjectTwcGeDetailFormKeys } from './projectTwcGeRequestDetailsPage.constants';
import { useFetchAllTwcGeRequestProposedAsOptions } from '../projectTwcGeRequestProposedAs/projectTwcGeRequestProposedAs.queries';

interface IProjectTwcGeRequestDetailPageProps {
  applicationType?: EProjectTwcGeRequestApplicationType;
}

export const ProjectTwcGeRequestDetailPage: React.FunctionComponent<IProjectTwcGeRequestDetailPageProps> = ({
  applicationType,
}) => {
  const { id, mode } = getAccessMode();
  const [uploadStatusMap, setUploadStatusMap] = React.useState<{ [key: string]: boolean }>({});
  const projectId = useProjectId();
  const isProjectArchived = useProjectIsArchived();

  const { navigate } = useNavigate();
  const {
    useFormMethods,
    isFormDirty,
    projectTwcGeRequest,
    errorGetProjectTwcGeRequest,
    isLoadingProjectTwcGeRequest,
    isRefetchingProjectTwcGeRequest,
    upsertButtonsProps,
    upsertErrors,
    hasIssueRight,
    hasEmApproveRight,
    hasHoeApproveRight,
    hasCvDownloadRight,
    hasCvUploadRight,
    setIsUploadingCv,
    isGeRequestAndProposedAsTwc,
    shouldReadOnly,
    projectUserProfileOptions,
  } = useUpsertTwcGeRequest(projectId, mode, id, applicationType);

  React.useEffect(() => {
    setIsUploadingCv(Object.values(uploadStatusMap).some((status) => status === true));
  }, [JSON.stringify(uploadStatusMap)]);

  const applicationTypeName = React.useMemo(() => {
    const type = projectTwcGeRequest?.applicationType || applicationType;
    if (!type) return 'User Role Request';
    return `${type === EProjectTwcGeRequestApplicationType.TWC ? 'TWC' : 'GE'} Request`;
  }, [applicationType, JSON.stringify(projectTwcGeRequest)]);

  const { data: proposedAsOptions, error: errorAllTwcGeRequestProposedAsOptions } =
    useFetchAllTwcGeRequestProposedAsOptions({
      isDirty: isFormDirty,
      shouldConvertOptions: true,
    })({ projectId });

  React.useEffect(() => {
    if (!projectTwcGeRequest) {
      useFormMethods.reset();
      return;
    }

    useFormMethods.setValue(ProjectTwcGeDetailFormKeys.NOMINATED_STAFF, projectTwcGeRequest.projectUserProfile);
    useFormMethods.setValue(ProjectTwcGeDetailFormKeys.POSITION, projectTwcGeRequest.position);
    useFormMethods.setValue(ProjectTwcGeDetailFormKeys.GRADE, projectTwcGeRequest.grade);
    useFormMethods.setValue(ProjectTwcGeDetailFormKeys.RESPONSIBILITY, projectTwcGeRequest.responsibility);
    useFormMethods.setValue(ProjectTwcGeDetailFormKeys.TRAINING_EVIDENCE, projectTwcGeRequest.trainingEvidence);
    useFormMethods.setValue(ProjectTwcGeDetailFormKeys.PM_COMMENTS, projectTwcGeRequest.pmComments);
    useFormMethods.setValue(ProjectTwcGeDetailFormKeys.EM_COMMENTS, projectTwcGeRequest.emComments);
    useFormMethods.setValue(ProjectTwcGeDetailFormKeys.HOE_COMMENTS, projectTwcGeRequest.hoeComments);
    useFormMethods.setValue(ProjectTwcGeDetailFormKeys.PROPOSED_AS, projectTwcGeRequest.proposedAs);
  }, [JSON.stringify(projectTwcGeRequest)]);

  React.useEffect(() => {
    if (!proposedAsOptions || proposedAsOptions.length <= 0) return;
    const temp = !projectTwcGeRequest
      ? proposedAsOptions[0]
      : proposedAsOptions.find((o) => o.key === projectTwcGeRequest.proposedAs);
    useFormMethods.setValue(ProjectTwcGeDetailFormKeys.PROPOSED_AS, [temp]);
  }, [JSON.stringify(projectTwcGeRequest), JSON.stringify(proposedAsOptions)]);

  const getStatusString = (status: EProjectTwcGeRequestStatus, lastModifiedDate: string) => {
    switch (status) {
      case EProjectTwcGeRequestStatus.DRAFT:
        return 'Draft';
      case EProjectTwcGeRequestStatus.WAITING_FOR_PM:
        return 'Waiting for Project Manager Nomination';
      case EProjectTwcGeRequestStatus.REJECTED_BY_PM:
        return `Project Manager Rejected (On ${lastModifiedDate})`;
      case EProjectTwcGeRequestStatus.WAITING_FOR_EM:
        return 'Waiting for approval of Engineering Manager';
      case EProjectTwcGeRequestStatus.REJECTED_BY_EM:
        return `Engineering Manager Rejected (On ${lastModifiedDate})`;
      case EProjectTwcGeRequestStatus.WAITING_FOR_HOE:
        return 'Waiting for approval of Head of Engineering';
      case EProjectTwcGeRequestStatus.REJECTED_BY_HOE:
        return `Head of Engineering Rejected (On ${lastModifiedDate})`;
      case EProjectTwcGeRequestStatus.COMPLETED:
        return 'Application Approved';
      case EProjectTwcGeRequestStatus.CANCELLED:
        return `Application Cancelled (On ${lastModifiedDate})`;
      case EProjectTwcGeRequestStatus.REVOKE:
        return `Revoked (On ${lastModifiedDate})`;
      default:
        return undefined;
    }
  };

  const rightTopBarItems = React.useMemo(() => {
    if (!projectTwcGeRequest) return [];
    let componentType = ComponentType.Dark;
    const statusStr = getStatusString(
      projectTwcGeRequest.status,
      convertISOStringToLocalDateString(projectTwcGeRequest.lastModifiedDateTime),
    );

    if (!statusStr) return [];

    switch (projectTwcGeRequest.status) {
      case EProjectTwcGeRequestStatus.REJECTED_BY_PM:
      case EProjectTwcGeRequestStatus.REJECTED_BY_EM:
      case EProjectTwcGeRequestStatus.REJECTED_BY_HOE:
      case EProjectTwcGeRequestStatus.CANCELLED:
      case EProjectTwcGeRequestStatus.REVOKE:
        componentType = ComponentType.Danger;
        break;
      case EProjectTwcGeRequestStatus.COMPLETED:
        componentType = ComponentType.Success;
        break;
      default:
        componentType = ComponentType.Dark;
        break;
    }

    return [
      {
        type: EDetailPageTopBarItemType.text,
        componentType,
        title: statusStr,
      },
    ];
  }, [JSON.stringify(projectTwcGeRequest)]);

  const isTwcApplication = React.useMemo(
    () =>
      (mode === 'Create' && applicationType === EProjectTwcGeRequestApplicationType.TWC) ||
      (projectTwcGeRequest && projectTwcGeRequest.applicationType === EProjectTwcGeRequestApplicationType.TWC),
    [mode, applicationType, JSON.stringify(projectTwcGeRequest)],
  );

  const isWithHoe = React.useMemo(
    () => isGeRequestAndProposedAsTwc || (projectTwcGeRequest && (projectTwcGeRequest.hoeProjectProfileId || 0) > 0),
    [isGeRequestAndProposedAsTwc],
  );
  return (
    <Scrollbar>
      <ApiErrorDisplay
        setFormError={(
          name: string,
          error: ErrorOption,
          options?:
            | {
                shouldFocus: boolean;
              }
            | undefined,
        ) => {
          useFormMethods.setError(name, error, options);
        }}
        errors={[
          ...upsertErrors,
          { error: errorAllTwcGeRequestProposedAsOptions, genericError: 'Get proposed options failed' },
          { error: errorGetProjectTwcGeRequest, genericError: `Get ${applicationTypeName} failed` },
        ]}
      />
      <DetailPageLayout
        errorGetDetail={errorGetProjectTwcGeRequest}
        isDrawer={false}
        data={mode === 'Create' ? {} : projectTwcGeRequest}
        isRefetching={isRefetchingProjectTwcGeRequest}
        isLoading={isLoadingProjectTwcGeRequest}
        title={`${applicationTypeName}${
          mode !== 'Create' && projectTwcGeRequest?.applicationNo ? ` - ${projectTwcGeRequest?.applicationNo}` : ''
        }`}
        breadcrumb={[
          {
            text: 'Home',
            prefixIconName: 'House',
            onClick: () => {
              navigate(routes.home.path);
            },
          },
          {
            prefixIconName: 'People',
            text: 'Users',
            onClick: () => {
              navigate(routes.users.path);
            },
          },
          {
            prefixIconName: 'People',
            text: `${applicationTypeName} List`,
            onClick: () => {
              if ((projectTwcGeRequest?.applicationType || applicationType) === EProjectTwcGeRequestApplicationType.TWC)
                navigate(routes.users.twcRequest.list.path);
              else navigate(routes.users.geRequest.list.path);
            },
          },
          {
            prefixIconName: 'People',
            text: applicationTypeName,
            active: true,
          },
        ]}
        render={() => {
          return (
            <Form
              useFormMethods={useFormMethods}
              render={(formFieldMethods) => {
                return (
                  <>
                    <DetailPageTopBar
                      leftItems={upsertButtonsProps as (IDetailPageTopBarItem | React.ReactElement)[]}
                      rightItems={rightTopBarItems}
                    />
                    <Grid
                      container
                      spacing={1.5}
                      columnSpacing={2}
                      className="gcl-gems-project-twc-ge-request-detail-page-container"
                    >
                      <Grid item xs={12} md={6} lg={4}>
                        <LabelWithMessage labelFor={ProjectTwcGeDetailFormKeys.NOMINATED_STAFF} text="Name" required />
                        {/* <FormSelectAsyncPaginate
                          shouldDebounce
                          pageSize={DEFAULT_SELECT_PAGE_SIZE}
                          rules={requiredRule}
                          placeholder="Select Nominated Staff..."
                          displayFieldName="displayName"
                          formFieldMethods={formFieldMethods}
                          useFetchOptions={useFetchProjectUserProfileOptionsWithSearch({ shouldConvertOptions: false })}
                          useFetchOptionsProps={{ projectId }}
                          id={ProjectTwcGeDetailFormKeys.NOMINATED_STAFF}
                          disabled={mode !== 'Create'}
                        /> */}
                        <FormSelect
                          options={projectUserProfileOptions?.options ?? []}
                          id={ProjectTwcGeDetailFormKeys.NOMINATED_STAFF}
                          formFieldMethods={formFieldMethods}
                          disabled={mode !== 'Create' || shouldReadOnly}
                          displayFieldName="displayName"
                          rules={requiredRule}
                          placeholder="Select Nominated Staff..."
                        />
                      </Grid>
                      <Grid item xs={12} md={6} lg={4}>
                        <LabelWithMessage
                          labelFor={ProjectTwcGeDetailFormKeys.POSITION}
                          text="Position"
                          required={isTwcApplication}
                        />
                        <FormTextField
                          formFieldMethods={formFieldMethods}
                          id={ProjectTwcGeDetailFormKeys.POSITION}
                          disabled={shouldReadOnly}
                          rules={isTwcApplication ? requiredRule : undefined}
                        />
                      </Grid>
                      <Grid item xs={12} lg={4}>
                        <LabelWithMessage
                          labelFor={ProjectTwcGeDetailFormKeys.GRADE}
                          text="Grade"
                          required={isTwcApplication}
                        />
                        <FormTextField
                          formFieldMethods={formFieldMethods}
                          id={ProjectTwcGeDetailFormKeys.GRADE}
                          disabled={shouldReadOnly}
                          rules={isTwcApplication ? requiredRule : undefined}
                        />
                      </Grid>
                      <Grid item xs={12} className={isGeRequestAndProposedAsTwc ? 'gcl-hide' : ''}>
                        <LabelWithMessage
                          labelFor={ProjectTwcGeDetailFormKeys.PROPOSED_AS}
                          text="Proposed As"
                          required={isTwcApplication}
                        />
                        <FormCheckboxGroup
                          itemPerRow={1}
                          checkOptions={proposedAsOptions || []}
                          type={CheckboxType.Radio}
                          formFieldMethods={formFieldMethods}
                          id={ProjectTwcGeDetailFormKeys.PROPOSED_AS}
                          rules={isTwcApplication ? requiredRule : undefined}
                          disabled={shouldReadOnly}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <LabelWithMessage
                          labelFor={ProjectTwcGeDetailFormKeys.RESPONSIBILITY}
                          text="Scope of Responsibility"
                          message="e.g. Entire project, timber platforms only, etc."
                          required={isTwcApplication}
                        />
                        <FormTextareaWithMaxLength
                          disabled={shouldReadOnly}
                          max={300}
                          id={ProjectTwcGeDetailFormKeys.RESPONSIBILITY}
                          formFieldMethods={formFieldMethods}
                          rules={isTwcApplication ? requiredRule : undefined}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <LabelWithMessage
                          labelFor={ProjectTwcGeDetailFormKeys.TRAINING_EVIDENCE}
                          text="Provide Evidence of Training on Effective Co-ordination of Temporary Works"
                          message="e.g. Gammon Academy course TCS-ETW301, project specific training, etc."
                          required={isTwcApplication}
                        />
                        <FormTextareaWithMaxLength
                          disabled={shouldReadOnly}
                          max={300}
                          id={ProjectTwcGeDetailFormKeys.TRAINING_EVIDENCE}
                          formFieldMethods={formFieldMethods}
                          rules={isTwcApplication ? requiredRule : undefined}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Attachments
                          className="gcl-mt-md gcl-mb-md"
                          mode={isProjectArchived ? 'View' : mode}
                          formFieldMethods={formFieldMethods}
                          formKeyToUpdateInCreateMode={ProjectTwcGeDetailFormKeys.ATTACHED_CV}
                          id="ProjectTwcGeRequestCv"
                          onUploadStatusChanged={(uuid, isUploading) => {
                            setUploadStatusMap((old) => {
                              return { ...old, [uuid]: isUploading };
                            });
                          }}
                          shouldHaveAtLeastOneFile
                          headerComponent={
                            <>
                              <LabelWithMessage labelFor="Files" text="Attached CV" required />
                              <Note componentType={ComponentType.Primary} prefixIcon={<Icon iconName="Lightbulb" />}>
                                <>
                                  <b>About Your CV</b>
                                  <ul className="gcl-gems-project-twc-ge-request-detail-page-cv-note">
                                    <li>
                                      Your year of birth is required, but please do not include additional personal
                                      data, e.g. your address and exact date of birth
                                    </li>
                                    <li>
                                      The CV would only be available for the one who upload it and the nominated staff
                                      during the draft state
                                    </li>
                                    <li>
                                      Once the request is issued to PM, the CV would only be available for the one who
                                      needs to approve/review the application, i.e. Nominated Staff, Project Manager,
                                      Engineering Manager and Head of Engineering
                                    </li>
                                  </ul>
                                </>
                              </Note>
                            </>
                          }
                          fileType={EProjectFileType.TWC_GE_REQUEST}
                          uploadFileRequestParam={{ projectTwcGeRequestId: projectTwcGeRequest?.id }}
                          useFetchFiles={useFetchAttachmentTwcGeRequests}
                          useFetchFilesProps={{
                            projectId,
                            projectTwcGeRequestId: id ?? 0,
                            fileType: EProjectFileType.TWC_GE_REQUEST,
                          }}
                          useFetchFilesQueryKey={[
                            projectId,
                            projectTwcGeRequest?.id || 0,
                            FETCH_QUERIES_NAME.PROJECT_FILE_ATTACHMENTS_TWC_GE_REQUESTS,
                            EProjectFileType.TWC_GE_REQUEST,
                          ]}
                          hasDownloadRight={hasCvDownloadRight}
                          hasUploadRight={hasCvUploadRight}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <ProjectTwcGeRequestDetailSignatureCard
                          formFieldMethods={formFieldMethods}
                          title="Nomination of TWC/TWCM"
                          description="The above named person is proposed as the TWC/TWCM to carry out duties in accordance with Gammon Quality Manual PDS/05."
                          signatureLabel="Proposed by PM"
                          approver={projectTwcGeRequest?.pmProjectUserProfile}
                          approvedDate={projectTwcGeRequest?.pmDate}
                          commentFormKey={ProjectTwcGeDetailFormKeys.PM_COMMENTS}
                          readonly={
                            !(
                              !!projectTwcGeRequest &&
                              projectTwcGeRequest.status === EProjectTwcGeRequestStatus.WAITING_FOR_PM &&
                              hasIssueRight
                            )
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <ProjectTwcGeRequestDetailSignatureCard
                          formFieldMethods={formFieldMethods}
                          title={isWithHoe ? 'Nomination of TWC/TWCM' : 'Approval of TWC/TWCM'}
                          description={
                            isWithHoe
                              ? 'The above named person is proposed as the TWC/TWCM to carry out duties in accordance with Gammon Quality Manual PDS/05.'
                              : 'The above named person is suitable for the position TWC/TWCM.'
                          }
                          signatureLabel={isWithHoe ? 'Seconded by EM' : 'Approved by EM'}
                          approver={projectTwcGeRequest?.emProjectUserProfile}
                          approvedDate={projectTwcGeRequest?.emDate}
                          commentFormKey={ProjectTwcGeDetailFormKeys.EM_COMMENTS}
                          readonly={
                            !(
                              !!projectTwcGeRequest &&
                              projectTwcGeRequest.status === EProjectTwcGeRequestStatus.WAITING_FOR_EM &&
                              hasEmApproveRight
                            )
                          }
                        />
                      </Grid>
                      {isWithHoe && (
                        <Grid item xs={12}>
                          <ProjectTwcGeRequestDetailSignatureCard
                            formFieldMethods={formFieldMethods}
                            title="Approval of TWC/TWCM"
                            description="The above named person is suitable for the position TWC/TWCM."
                            signatureLabel="Approved by HOE"
                            approver={projectTwcGeRequest?.hoeProjectUserProfile}
                            approvedDate={projectTwcGeRequest?.hoeDate}
                            commentFormKey={ProjectTwcGeDetailFormKeys.HOE_COMMENTS}
                            readonly={
                              !(
                                !!projectTwcGeRequest &&
                                projectTwcGeRequest.status === EProjectTwcGeRequestStatus.WAITING_FOR_HOE &&
                                hasHoeApproveRight
                              )
                            }
                          />
                        </Grid>
                      )}
                    </Grid>
                  </>
                );
              }}
            />
          );
        }}
      />
    </Scrollbar>
  );
};
